import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Info, Phone, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";
import { TEL_HREF, PHONE_DISPLAY, WA_HREF_TEXT, WA_HREF_TEXT_EN } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = [];
for (let h = 10; h <= 23; h++) {
  TIMES.push(`${String(h).padStart(2, "0")}:00`, `${String(h).padStart(2, "0")}:30`);
}

const inputCls =
  "w-full rounded-lg border border-stoneMuted/30 bg-cream px-4 py-3 text-sm text-ink outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25";
const labelCls = "block text-[11px] font-mono uppercase tracking-[0.18em] text-stoneMuted mb-1.5";

export const Booking = () => {
  const { t, lang, isEn } = useLanguage();
  const empty = { name: "", email: "", phone: "", date: "", time: "", guests: "2", experience: "aperitivo", notes: "", website: "" };
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.phone || !form.date || !form.time) {
      toast.error(t.booking.errRequired);
      return;
    }
    if (!consent) {
      toast.error(t.booking.errConsent);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, guests: Number(form.guests), consent, lang });
      setDone(true);
      toast.success(t.booking.successTitle);
    } catch (err) {
      if (err?.response?.status === 429) toast.error(t.booking.errRate);
      else toast.error(t.booking.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="prenota" className="relative py-24 sm:py-32 bg-cream overflow-hidden">
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute bottom-0 left-0 font-serif italic text-[10rem] sm:text-[15rem] leading-none text-stroke-ghost opacity-50"
      >
        05
      </span>

      <div className="relative max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <Eyebrow>Prenotazioni & Contatti</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-ink">
              {t.booking.title}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-base sm:text-lg font-light leading-relaxed text-stone-600">{t.booking.subtitle}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-7 flex items-start gap-3 rounded-xl border border-gold/40 bg-warm p-5">
              <Info className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed text-stone-600">{t.booking.note}</p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={TEL_HREF}
                data-testid="booking-call-button"
                className="inline-flex items-center gap-2 rounded-full bg-burgundy px-6 py-3 text-sm font-semibold text-cream hover:bg-burgundy-light transition-colors hover:scale-[1.03] active:scale-[0.98]"
              >
                <Phone className="w-4 h-4" /> {t.booking.call} · {PHONE_DISPLAY}
              </a>
              <a
                href={isEn ? WA_HREF_TEXT_EN : WA_HREF_TEXT}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-whatsapp-button"
                className="inline-flex items-center gap-2 rounded-full border border-stoneMuted/30 px-6 py-3 text-sm font-medium text-ink hover:border-gold hover:text-burgundy transition-colors hover:scale-[1.03] active:scale-[0.98]"
              >
                <MessageCircle className="w-4 h-4" /> {t.booking.whatsapp}
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative rounded-2xl border border-gold/35 bg-warm p-6 sm:p-10 shadow-[0_20px_40px_-15px_rgba(59,10,17,0.15)]">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="py-10 text-center"
                  data-testid="booking-success-panel"
                >
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-burgundy">
                    <Check className="w-8 h-8 text-gold" />
                  </span>
                  <h3 className="mt-6 font-serif text-3xl text-ink">{t.booking.successTitle}</h3>
                  <p className="mt-4 text-sm sm:text-base leading-relaxed text-stone-600 max-w-sm mx-auto">
                    {t.booking.successText}
                  </p>
                  <button
                    onClick={() => {
                      setForm(empty);
                      setConsent(false);
                      setDone(false);
                    }}
                    data-testid="booking-again-button"
                    className="mt-8 rounded-full border border-burgundy px-6 py-3 text-sm font-medium text-burgundy hover:bg-burgundy hover:text-cream transition-colors"
                  >
                    {t.booking.again}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={onSubmit}
                  noValidate
                  data-testid="booking-request-form"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                >
                  <div className="sm:col-span-1">
                    <label className={labelCls} htmlFor="bk-name">{t.booking.fields.name}</label>
                    <input id="bk-name" data-testid="booking-name-input" className={inputCls} value={form.name} onChange={set("name")} required />
                  </div>
                  <div className="sm:col-span-1">
                    <label className={labelCls} htmlFor="bk-email">{t.booking.fields.email}</label>
                    <input id="bk-email" type="email" data-testid="booking-email-input" className={inputCls} value={form.email} onChange={set("email")} required />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bk-phone">{t.booking.fields.phone}</label>
                    <input id="bk-phone" type="tel" data-testid="booking-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} required />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bk-guests">{t.booking.fields.guests}</label>
                    <select id="bk-guests" data-testid="booking-guests-select" className={inputCls} value={form.guests} onChange={set("guests")}>
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1} {t.booking.guestsSuffix}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bk-date">{t.booking.fields.date}</label>
                    <input id="bk-date" type="date" min={today} data-testid="booking-date-input" className={inputCls} value={form.date} onChange={set("date")} required />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bk-time">{t.booking.fields.time}</label>
                    <select id="bk-time" data-testid="booking-time-input" className={inputCls} value={form.time} onChange={set("time")} required>
                      <option value="">--:--</option>
                      {TIMES.map((tm) => (
                        <option key={tm} value={tm}>{tm}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="bk-exp">{t.booking.fields.experience}</label>
                    <select id="bk-exp" data-testid="booking-experience-select" className={inputCls} value={form.experience} onChange={set("experience")}>
                      {["aperitivo", "degustazione", "cena", "calice"].map((k) => (
                        <option key={k} value={k}>{t.booking.experience[k]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="bk-notes">{t.booking.fields.notes}</label>
                    <textarea id="bk-notes" rows={3} data-testid="booking-notes-input" className={`${inputCls} resize-none`} value={form.notes} onChange={set("notes")} />
                  </div>

                  <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={consent}
                        data-testid="booking-gdpr-checkbox"
                        onClick={() => setConsent((v) => !v)}
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
                          consent ? "border-burgundy bg-burgundy" : "border-stoneMuted/40 bg-cream"
                        }`}
                      >
                        {consent && <Check className="w-3.5 h-3.5 text-gold" />}
                      </button>
                      <span className="text-xs leading-relaxed text-stone-600">{t.booking.gdpr}</span>
                    </label>
                  </div>

                  <div className="sm:col-span-2">
                    <motion.button
                      type="submit"
                      data-testid="booking-submit-button"
                      disabled={loading}
                      whileHover={{ scale: loading ? 1 : 1.02 }}
                      whileTap={{ scale: loading ? 1 : 0.98 }}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-burgundy px-8 py-4 text-sm font-semibold tracking-wide text-cream transition-colors hover:bg-burgundy-light disabled:opacity-60"
                    >
                      <Send className="w-4 h-4" />
                      {loading ? t.booking.submitting : t.booking.submit}
                    </motion.button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
